"use client";
import React, { useEffect, useRef, useState } from "react";
import gs, { colors } from "gstyles";
import Link from "components/common/Link";
import { Search, ChevronLeft, Share2, Store } from "lucide-react";
import { message } from "antd";
import LanguageSwitcher from "components/common/LanguageSwitcher";
import BookingQRModal from "components/common/BookingQRModal";
import MobileMenu from "../MobileMenu";
import { useI18n, useT } from "providers/I18nProvider";
import { localizePath } from "utils/locale";

const SUGGEST_KEYS = [
  "header.suggest1",
  "header.suggest2",
  "header.suggest3",
  "header.suggest4",
];

/**
 * compact = header trang chi tiết shop: trên mobile chỉ còn 1 hàng 52px
 * (‹ quay lại · logo · chia sẻ · ☰), KHÔNG ô tìm kiếm — đã có tab Tìm kiếm ở đáy
 * (chốt mockup trang shop v2 với chị Naomi 05/10). Desktop không đổi.
 */
const Header = ({ compact = false }: { compact?: boolean }) => {
  const t = useT();
  const { locale } = useI18n();
  const [idx, setIdx] = useState(0);
  const [focused, setFocused] = useState(false);
  const [openQR, setOpenQR] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Placeholder gợi ý xoay vòng
  useEffect(() => {
    if (focused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % SUGGEST_KEYS.length), 2600);
    return () => clearInterval(id);
  }, [focused]);

  const onSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = inputRef.current?.value?.trim();
    if (value) {
      window.location.href = `${localizePath("/search", locale)}?searchTerm=${encodeURIComponent(value)}`;
    }
  };

  const goBack = () => {
    if (typeof window === "undefined") return;
    // Chỉ cần có lịch sử là back được; không dựa document.referrer vì referrer
    // bị trống khi Referrer-Policy chặn / mở từ app khác → từng đẩy nhầm về trang chủ.
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = localizePath("/", locale);
    }
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: document.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      message.success(t("shopInfo.linkCopied"));
    } catch {
      /* người dùng huỷ chia sẻ */
    }
  };

  const hClass = compact
    ? "max-md:h-[52px] h-[var(--header-height)]"
    : "h-[var(--header-height)]";

  return (
    <div className={`w-full ${hClass}`}>
      <div className="fixed top-0 left-0 w-full h-[460px] bg-primary z-[-1]"></div>
      <header className={`relative w-full flex items-center bg-primary ${hClass}`}>
        {/* Mobile: hàng 1 = logo + hamburger, hàng 2 = ô tìm kiếm full ngang. md+: 1 hàng như cũ */}
        <div className="container mx-auto px-4 md:px-6 flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 md:gap-6">
          {compact && (
            <button
              type="button"
              onClick={goBack}
              aria-label={t("shopInfo.back")}
              className="md:hidden -ml-2 flex h-10 w-10 items-center justify-center text-white"
            >
              <ChevronLeft size={26} />
            </button>
          )}
          {/* Logo */}
          <div className="sm:hidden shrink-0">
            <Link href="/">
              {gs.icons({ name: "logo-black", width: 100, color: colors.white })}
            </Link>
          </div>
          <div className="hidden sm:block shrink-0">
            <Link href="/">
              {gs.icons({ name: "logo-black", width: 140, color: colors.white })}
            </Link>
          </div>

          {/* Ô tìm kiếm lớn — canh giữa header */}
          <div className={`order-last w-full md:order-none md:w-auto md:flex-1 justify-center ${compact ? "hidden md:flex" : "flex"}`}>
            <form
              className="w-full max-w-[520px]"
              onSubmit={onSearch}
              role="search"
            >
              <div
                className={`relative flex items-center rounded-full bg-white/20 transition-all duration-200 ${
                  focused
                    ? "bg-white shadow-lg ring-2 ring-white/60"
                    : "hover:bg-white/30"
                }`}
              >
                <Search
                  size={20}
                  color={focused ? colors.primary : colors.white}
                  className="ml-4 shrink-0"
                />
                <input
                  ref={inputRef}
                  type="text"
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  placeholder={
                    focused
                      ? t("header.searchPlaceholder")
                      : `${t("header.searchPrefix")} ${t(SUGGEST_KEYS[idx])}`
                  }
                  aria-label={t("header.searchPlaceholder")}
                  className={`w-full bg-transparent px-3 py-2.5 text-[16px] md:text-[15px] min-w-0 focus:outline-none ${
                    focused
                      ? "text-gray-900 placeholder-gray-400"
                      : "text-white placeholder-white/75"
                  }`}
                />
                <button
                  type="submit"
                  className={`tap-area mr-1.5 shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                    focused
                      ? "bg-primary text-white"
                      : "bg-white/90 text-primary hover:bg-white"
                  }`}
                >
                  {t("header.searchButton")}
                </button>
              </div>
            </form>
          </div>

          <div className="ml-auto md:ml-0 shrink-0 flex items-center gap-3">
            {/* Nút Hợp tác: glass pill + icon cửa hàng, hover đảo màu sang trắng/teal
                → nhìn chuyên nghiệp và rõ là dành cho chủ spa/salon, nhưng vẫn
                nhẹ hơn nút "Tải App" (CTA chính) để không tranh nhau. */}
            <Link
              href="/hop-tac"
              className="group hidden lg:inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/60 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm shadow-sm transition-all duration-200 hover:border-white hover:bg-white hover:text-primary hover:shadow-md hover:-translate-y-[1px]"
            >
              <Store width={16} height={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
              <span>{t("header.partner")}</span>
              <span className="transition-transform duration-200 group-hover:translate-x-0.5">
                →
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setOpenQR(true)}
              className="hidden md:inline-flex items-center whitespace-nowrap rounded-full bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm transition-transform hover:scale-[1.03] active:scale-95"
            >
              {t("header.downloadApp")}
            </button>
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            {compact && (
              <button
                type="button"
                onClick={onShare}
                aria-label={t("shopInfo.share")}
                className="md:hidden flex h-10 w-10 items-center justify-center text-white"
              >
                <Share2 size={20} />
              </button>
            )}
            <MobileMenu />
          </div>
        </div>
      </header>

      <BookingQRModal open={openQR} onClose={() => setOpenQR(false)} />
    </div>
  );
};

export default Header;
